'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { amplitude } from '@/libs/amplitude';
import { cn } from '@/libs/utils';
import { Button } from '@/shared/components/Button';
import { buildRegistrationUsernameFromEmail, buildRegistrationUsernameFromPhone } from '../helpers/buildRegistrationUsername';
import { normalizePhone } from '../helpers/normalizePhone';
import { useRegister } from '../hooks/useRegister';
import { PhoneCodeForm } from '../PhoneCodeForm';
import { AuthPasswordInput } from '../ui/AuthPasswordInput';
import { EmailPhoneInput } from '../ui/EmailPhoneInput';
import { signUpEmailSchema, signUpPhoneSchema } from './shema';
type SignUpMode = 'email' | 'phone';
export type SignUpEmailFormValues = {
  email: string;
  password: string;
  confirmPassword: string;
};
export type SignUpPhoneFormValues = {
  phone: string;
  password: string;
  confirmPassword: string;
};
export const SignUpForm = () => {
  const [step, setStep] = useState<'form' | 'phone' | 'email'>('form');
  const [signUpMode, _setSignUpMode] = useState<SignUpMode>('email');
  const [submittedPhone, setSubmittedPhone] = useState<string>('');
  // const [submittedEmail, setSubmittedEmail] = useState<string>("");
  const searchParams = useSearchParams();
  const referralCode = useMemo(() => (searchParams.get('refId') || searchParams.get('refid')) as string | null, [searchParams]);
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('Auth');
  const {
    registerEmail,
    registerPhone,
    isEmailRegistering,
    isPhoneRegistering
  } = useRegister();
  const emailForm = useForm<SignUpEmailFormValues>({
    mode: 'onChange',
    resolver: zodResolver(signUpEmailSchema(t)),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: ''
    }
  });
  const phoneForm = useForm<SignUpPhoneFormValues>({
    mode: 'onChange',
    resolver: zodResolver(signUpPhoneSchema(t)),
    defaultValues: {
      phone: '',
      password: '',
      confirmPassword: ''
    }
  });

  // Реактивная валидация confirmPassword при изменении password для email формы
  const emailPassword = emailForm.watch('password');
  const emailConfirmPassword = emailForm.watch('confirmPassword');
  useEffect(() => {
    if (emailConfirmPassword) {
      emailForm.trigger('confirmPassword');
    }
  }, [emailPassword, emailConfirmPassword, emailForm]);

  // Реактивная валидация confirmPassword при изменении password для phone формы
  const phonePassword = phoneForm.watch('password');
  const phoneConfirmPassword = phoneForm.watch('confirmPassword');
  useEffect(() => {
    if (phoneConfirmPassword) {
      phoneForm.trigger('confirmPassword');
    }
  }, [phonePassword, phoneConfirmPassword, phoneForm]);
  const onSubmitEmail = async (data: SignUpEmailFormValues) => {
    emailForm.clearErrors();
    const username: string = buildRegistrationUsernameFromEmail(data.email);
    const result = await registerEmail({
      email: data.email,
      username,
      password: data.password,
      referralCode: referralCode || undefined
    });
    if (!result.success) {
      // Показываем ошибку на поле email
      emailForm.setError('email', {
        message: result.error
      });
    } else {
      amplitude.setUserId(result.data?.user?.id);
      const identifyObj = new amplitude.Identify().set('email', data.email).set('username', username).set('registrationDate', new Date().toISOString());
      amplitude.identify(identifyObj);
      amplitude.track('SignUp', {
        method: 'Email'
      });
      // Успешная регистрация - сохраняем email и переходим к подтверждению
      // setSubmittedEmail(data.email);
      setStep('email');
    }
  };
  const onSubmitPhone = async (data_0: SignUpPhoneFormValues) => {
    phoneForm.clearErrors();
    const normalizedPhone: string = normalizePhone(data_0.phone);
    const username_0: string = buildRegistrationUsernameFromPhone(normalizedPhone);
    const result_0 = await registerPhone({
      phone: normalizedPhone,
      username: username_0,
      password: data_0.password,
      referralCode: referralCode || undefined
    });
    if (!result_0.success) {
      // Показываем ошибку на поле phone
      phoneForm.setError('phone', {
        message: result_0.error
      });
    } else {
      amplitude.setUserId(result_0.data?.user?.id);
      const identifyObj_0 = new amplitude.Identify().set('phone', data_0.phone).set('username', username_0).set('registrationDate', new Date().toISOString());
      amplitude.identify(identifyObj_0);
      amplitude.track('SignUp', {
        method: 'Phone'
      });
      // Успешная регистрация - сохраняем телефон и переходим к подтверждению OTP
      setSubmittedPhone(data_0.phone);
      setStep('phone');
    }
  };
  const onSubmitting = async (_data: {
    otp: string;
  }, _setError?: (message: string) => void) => {
    // TODO: Добавить верификацию OTP через новый API когда будет готов endpoint
    // Пока просто редиректим после ввода кода (верификация на бэкенде)
    router.push(`/${locale}/auth`);
  };
  const renderContent = () => {
    if (step === 'form') {
      return <>
          <div className="flex flex-col items-center justify-center">
            <div className="flex w-full flex-col items-center justify-center">
              <h1 className="text-fill-transparent bold tablet:leading-1 w-full 
                bg-gradient-to-r from-[#DA3D15] to-[#FE7801] bg-clip-text 
                text-center font-secondary text-[30px] font-extrabold uppercase italic 
                leading-none tablet:text-nowrap tablet:text-[34px]">
                {t('CreateAccount')}
              </h1>
              <div className="flex flex-col items-center justify-center gap-1">
                <Link href={`/${locale}/auth`}>
                  <span className="cursor-pointer text-[30px] font-extrabold uppercase italic text-white tablet:text-[34px]">
                    {t('OrLogIn')}
                  </span>
                </Link>
                <div className="h-[4px] w-full bg-white [clip-path:polygon(0_100%,3%_0,100%_0,97%_100%)]"></div>
              </div>
            </div>
          </div>

          {/* Табы для переключения режима */}
          {/* <div className="flex w-full gap-2 rounded-lg bg-[#FFFFFF0F] p-1">
            <button
              type="button"
              onClick={() => handleTabChange('email')}
              className={cn(
                'flex-1 rounded-md py-2.5 font-secondary text-[16px] font-bold uppercase transition-colors',
                signUpMode === 'email'
                  ? 'bg-gradient-to-r from-[#DA3D15] to-[#FE7801] text-white'
                  : 'text-[#AAAAB6] hover:text-white'
              )}
            >
              {t('Email')}
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('phone')}
              className={cn(
                'flex-1 rounded-md py-2.5 font-secondary text-[16px] font-bold uppercase transition-colors',
                signUpMode === 'phone'
                  ? 'bg-gradient-to-r from-[#DA3D15] to-[#FE7801] text-white'
                  : 'text-[#AAAAB6] hover:text-white'
              )}
            >
              {t('Phone')}
            </button>
           </div> */}

          <div className={cn('flex w-full flex-col gap-6', signUpMode === 'email' ? '' : ' hidden')}>
            <form onSubmit={emailForm.handleSubmit(onSubmitEmail)} className={'flex w-full flex-col gap-6'}>
              <Controller control={emailForm.control} name="email" render={({
              field
            }) => <EmailPhoneInput label={t('Email')} type="email" errorMessage={emailForm.formState.errors.email?.message} {...field} placeholder={t('EmailPlaceholder')} />} />
              <div className="flex flex-col gap-2">
                <Controller control={emailForm.control} name="password" render={({
                field: field_0
              }) => <AuthPasswordInput label={t('Password')} placeholder={t('PasswordPlaceholder')} errorMessage={emailForm.formState.errors.password?.message} {...field_0} />} />
                <span className="text-[14px] text-[#AAAAB6]">{t('PasswordMustContain')}</span>
              </div>
              <Controller control={emailForm.control} name="confirmPassword" render={({
              field: field_1
            }) => <AuthPasswordInput label={t('ConfirmPassword')} placeholder={t('PasswordPlaceholder')} errorMessage={emailForm.formState.errors.confirmPassword?.message} {...field_1} />} />
            </form>
            <div className="flex w-full flex-col items-center gap-4">
              <Button size="2md" variant="white" className="w-full rounded-[14px]" onClick={emailForm.handleSubmit(onSubmitEmail)} loading={isEmailRegistering}>
                {t('SignUp')}
              </Button>
              <span className="text-[14px] text-[#AAAAB6]">
                {t('Terms.Continuing')}{' '}
                <Link className="text-white" href={`/${locale}/terms`}>
                  {t('Terms.Terms')}
                </Link>{' '}
                {t('Terms.and')}{' '}
                <Link className="text-white" href={`/${locale}/privacy-policy`}>
                  {t('Terms.PrivacyPolicy')}
                </Link>
                .
              </span>
              <span className="text-center text-[14px] text-[#AAAAB6]">{t('Captcha')}</span>
            </div>
          </div>
          <div className={cn('flex w-full flex-col gap-6', signUpMode !== 'email' ? '' : ' hidden')}>
            <form onSubmit={phoneForm.handleSubmit(onSubmitPhone)} className={cn('flex w-full flex-col gap-6', signUpMode !== 'email' ? '' : ' hidden')}>
              <Controller control={phoneForm.control} name="phone" render={({
              field: field_2
            }) => <EmailPhoneInput label={t('Phone')} type="phone" errorMessage={phoneForm.formState.errors.phone?.message} {...field_2} placeholder={t('PhonePlaceholder')} />} />
              <div className="flex flex-col gap-2">
                <Controller control={phoneForm.control} name="password" render={({
                field: field_3
              }) => <AuthPasswordInput label={t('Password')} placeholder={t('PasswordPlaceholder')} errorMessage={phoneForm.formState.errors.password?.message} {...field_3} />} />
                <span className="text-[14px] text-[#AAAAB6]">{t('PasswordMustContain')}</span>
              </div>
              <Controller control={phoneForm.control} name="confirmPassword" render={({
              field: field_4
            }) => <AuthPasswordInput label={t('ConfirmPassword')} placeholder={t('PasswordPlaceholder')} errorMessage={phoneForm.formState.errors.confirmPassword?.message} {...field_4} />} />
            </form>
            <div className="flex w-full flex-col items-center gap-4">
              <Button size="2md" variant="white" className="w-full rounded-[14px]" onClick={phoneForm.handleSubmit(onSubmitPhone)} loading={isPhoneRegistering}>
                {t('SignUp')}
              </Button>
              <span className="text-[14px] text-[#AAAAB6]">
                {t('Terms.Continuing')}{' '}
                <Link className="text-white" href={`/${locale}/terms`}>
                  {t('Terms.Terms')}
                </Link>{' '}
                {t('Terms.and')}{' '}
                <Link className="text-white" href={`/${locale}/privacy-policy`}>
                  {t('Terms.PrivacyPolicy')}
                </Link>
                .
              </span>
              <span className="text-center text-[14px] text-[#AAAAB6]">{t('Captcha')}</span>
            </div>
          </div>
        </>;
    }
    if (step === 'email') {
      return <>
          <div className=" flex flex-col items-center justify-center">
            <div className="flex w-full flex-col items-center justify-center gap-4">
              <h1 className="text-fill-transparent bold tablet:leading-1 w-full bg-gradient-to-r from-[#DA3D15] to-[#FE7801] bg-clip-text  text-center font-secondary text-[30px] font-extrabold uppercase italic 
    leading-none tablet:text-nowrap tablet:text-[34px] ">
                {t('CreateAccount')}
              </h1>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-4">
            <span className="text-center text-[18px] font-bold text-white">{t('CheckEmail.Title')}</span>
            <span className="text-center text-[16px] text-[#AAAAB6]">{t('CheckEmail.Description')}</span>
          </div>
        </>;
    }
    if (step === 'phone') {
      return <PhoneCodeForm phone={submittedPhone} caption={t('CheckPhone.Description')} onSubmitting={onSubmitting} />;
    }
  };
  return <div className="flex h-[100%] w-full flex-col items-center justify-center gap-6 p-4 py-6  tablet:gap-10 tablet:px-[60px] tablet:py-10">
      {renderContent()}
    </div>;
};